import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  ArrowLeftRight, 
} from 'lucide-react';
import SiteSelector from './SiteSelector';
import SiteManagement from './SiteManagement';
import UserAssignment from './UserAssignment';
import DashboardStats from './DashboardStats';
import sitesService from '../../services/sites';
import tenantUserService from '../../services/tenantUserService';
import authService from '../../services/auth';

interface Site {
  id: number;
  name: string;
  code: string;
  site_type: string;
  timezone: string;
  address: string;
  contact_phone: string;
  contact_email: string;
  is_active: boolean;
  tenant: number;
  tenant_name?: string;
  logo?: string;
  is_blocked: boolean;
  blocked_at?: string;
  blocked_by?: number;
  blocked_by_name?: string;
  block_reason?: string;
}

interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  is_blocked: boolean;
}

interface TenantUser {
  id: number;
  tenant_user_id?: number;
  user_id: number;
  tenant_id: number;
  role: 'TENANT_ADMIN' | 'TENANT_MANAGER' | 'TENANT_OWNER' | 'TENANT_USER';
  notes?: string;
  expires_at?: string;
  created_at: string;
  updated_at?: string;
  is_active?: boolean;
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
  };
  user_username?: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_is_active?: boolean;
  tenant_name?: string;
  tenant_code?: string;
  assigned_by_username?: string;
  role_display?: string;
}

const MultiSiteManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sites, setSites] = useState<Site[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSite, setCurrentSite] = useState<Site | null>(null);
  const [currentTenantUser, setCurrentTenantUser] = useState<TenantUser | null>(null);
  const [isTenantUser, setIsTenantUser] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [isSiteUser, setIsSiteUser] = useState(false);

  // Déterminer l'onglet actif basé sur les paramètres d'URL
  const getActiveTab = (): 'selector' | 'management' | 'assignment' => {
    const tab = searchParams.get('tab');
    if (tab === 'management' || tab === 'assignment') {
      return tab;
    }
    return 'selector';
  };

  const [activeTab, setActiveTab] = useState<'selector' | 'management' | 'assignment'>(getActiveTab());

  useEffect(() => {
    checkUserTenantStatus();
    fetchSites();
    fetchTenants();
  }, []);

  // Vérifier le type d'utilisateur (tenant ou site)
  const checkUserTenantStatus = async () => {
    try {
      // Récupérer le profil de l'utilisateur connecté
      const userProfile = await authService.getUserProfile();
      console.log('👤 Profil utilisateur:', userProfile);
      
      // Stocker le profil utilisateur
      setCurrentUserProfile(userProfile);
      
      const userRole = userProfile.role?.name;
      
      // Vérifier si l'utilisateur a un rôle de tenant
      const tenantRoles = ['TENANT_ADMIN', 'TENANT_MANAGER', 'TENANT_OWNER', 'TENANT_USER'];
      const siteRoles = ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'];
      
      if (userRole && tenantRoles.includes(userRole)) {
        console.log('🏢 Utilisateur avec rôle tenant détecté:', userRole);
        setIsTenantUser(true);
        setIsSiteUser(false);
        
        // Essayer de récupérer les informations de l'utilisateur tenant
        try {
          const tenantUserInfo = await tenantUserService.getCurrentTenantUser();
          if (tenantUserInfo) {
            setCurrentTenantUser(tenantUserInfo);
            console.log('🏢 Informations utilisateur tenant:', tenantUserInfo);
          }
        } catch (error) {
          console.warn('⚠️ Impossible de récupérer les informations tenant user:', error);
        }
      } else if (userRole && siteRoles.includes(userRole)) {
        console.log('🏭 Utilisateur avec rôle site détecté:', userRole);
        setIsTenantUser(false);
        setIsSiteUser(true);
        
        // Pour les utilisateurs site, on va filtrer selon leurs sites assignés
        // Les sites assignés sont disponibles dans userProfile.sites
        console.log('🏭 Sites assignés à l\'utilisateur:', userProfile.sites);
      } else {
        console.log('👑 Utilisateur avec rôle système:', userRole);
        setIsTenantUser(false);
        setIsSiteUser(false);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut utilisateur:', error);
      setIsTenantUser(false);
      setIsSiteUser(false);
    }
  };

  // Synchroniser l'onglet actif avec les paramètres d'URL
  useEffect(() => {
    const tab = getActiveTab();
    setActiveTab(tab);
  }, [searchParams]);

  // Recharger les données quand le statut de l'utilisateur change
  useEffect(() => {
    if (isTenantUser !== null || isSiteUser !== null) {
      fetchSites();
      fetchTenants();
    }
  }, [isTenantUser, isSiteUser, currentTenantUser, currentUserProfile]);

  // Fonction pour changer d'onglet et mettre à jour l'URL
  const handleTabChange = (tabId: 'selector' | 'management' | 'assignment') => {
    setActiveTab(tabId);
    if (tabId === 'selector') {
      setSearchParams({}); // Supprimer le paramètre tab pour l'onglet par défaut
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      const allSites = await sitesService.getSites();
      
      // Filtrer les sites selon le type d'utilisateur
      let filteredSites = allSites;
      
      if (isTenantUser && currentTenantUser) {
        // Utilisateur tenant : filtrer par tenant
        console.log('🔍 Filtrage des sites pour le tenant:', currentTenantUser.tenant_id);
        filteredSites = allSites.filter(site => {
          const siteTenantId = (site as any).tenant?.id || (site as any).tenant || 0;
          return siteTenantId === currentTenantUser.tenant_id;
        });
        console.log('📋 Sites filtrés par tenant:', filteredSites.length, 'sur', allSites.length);
      } else if (isTenantUser && !currentTenantUser) {
        // Si on est un utilisateur tenant mais qu'on n'a pas les infos tenant, 
        // essayer de récupérer le tenant depuis le localStorage
        const activeSiteInfo = localStorage.getItem('activeSiteInfo');
        if (activeSiteInfo) {
          try {
            const siteInfo = JSON.parse(activeSiteInfo);
            const tenantId = siteInfo.tenant?.id;
            if (tenantId) {
              console.log('🔍 Filtrage des sites pour le tenant depuis localStorage:', tenantId);
              filteredSites = allSites.filter(site => {
                const siteTenantId = (site as any).tenant?.id || (site as any).tenant || 0;
                return siteTenantId === tenantId;
              });
              console.log('📋 Sites filtrés depuis localStorage:', filteredSites.length, 'sur', allSites.length);
            }
          } catch (error) {
            console.warn('⚠️ Erreur lors du parsing des informations du site:', error);
          }
        }
      } else if (isSiteUser && currentUserProfile?.sites) {
        // Utilisateur site : filtrer par sites assignés
        console.log('🔍 Filtrage des sites pour l\'utilisateur site:', currentUserProfile.sites);
        const assignedSiteIds = currentUserProfile.sites.map((site: any) => site.id);
        filteredSites = allSites.filter(site => {
          return assignedSiteIds.includes(site.id);
        });
        console.log('📋 Sites filtrés par assignation:', filteredSites.length, 'sur', allSites.length);
        console.log('📋 IDs des sites assignés:', assignedSiteIds);
      }
      
      setSites(filteredSites.map(site => ({
        ...site,
        timezone: (site as any).timezone || '',
        address: (site as any).address || '',
        contact_phone: (site as any).contact_phone || '',
        contact_email: (site as any).contact_email || '',
        is_blocked: (site as any).is_blocked || false,
        tenant: (site as any).tenant?.id || (site as any).tenant || 0
      })));
    } catch (error) {
      console.error('Erreur lors de la récupération des sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const allTenants = await sitesService.getTenants();
      
      // Filtrer les tenants selon le type d'utilisateur
      let filteredTenants = allTenants;
      
      if (isTenantUser && currentTenantUser) {
        // Utilisateur tenant : filtrer par tenant
        console.log('🔍 Filtrage des tenants pour l\'utilisateur tenant:', currentTenantUser.tenant_id);
        filteredTenants = allTenants.filter(tenant => tenant.id === currentTenantUser.tenant_id);
        console.log('📋 Tenants filtrés par tenant user:', filteredTenants.length, 'sur', allTenants.length);
      } else if (isSiteUser && currentUserProfile?.sites) {
        // Utilisateur site : filtrer par tenants des sites assignés
        console.log('🔍 Filtrage des tenants pour l\'utilisateur site');
        const assignedSiteTenantIds = new Set();
        
        // Récupérer les IDs des tenants des sites assignés
        const allSites = await sitesService.getSites();
        const assignedSiteIds = currentUserProfile.sites.map((site: any) => site.id);
        
        allSites.forEach(site => {
          if (assignedSiteIds.includes(site.id)) {
            const siteTenantId = (site as any).tenant?.id || (site as any).tenant || 0;
            if (siteTenantId) {
              assignedSiteTenantIds.add(siteTenantId);
            }
          }
        });
        
        filteredTenants = allTenants.filter(tenant => assignedSiteTenantIds.has(tenant.id));
        console.log('📋 Tenants filtrés par sites assignés:', filteredTenants.length, 'sur', allTenants.length);
        console.log('📋 IDs des tenants des sites assignés:', Array.from(assignedSiteTenantIds));
      }
      
      setTenants(filteredTenants.map(tenant => ({ ...tenant, is_blocked: (tenant as any).is_blocked || false })));
    } catch (error) {
      console.error('Erreur lors de la récupération des tenants:', error);
    }
  };

  const handleSiteSwitch = async (siteCode: string) => {
    try {
      const result = await sitesService.switchSite(siteCode);
      
      if (result && result.site) {
        const site = {
          ...result.site,
          timezone: (result.site as any).timezone || '',
          address: (result.site as any).address || '',
          contact_phone: (result.site as any).contact_phone || '',
          contact_email: (result.site as any).contact_email || '',
          is_blocked: (result.site as any).is_blocked || false,
          tenant: (result.site as any).tenant?.id || (result.site as any).tenant || 0
        };
        setCurrentSite(site);
        localStorage.setItem('activeSite', JSON.stringify(site));
        // Rafraîchir les données
        fetchSites();
      }
    } catch (error) {
      console.error('Erreur lors du changement de site:', error);
    }
  };

  // Convert sites for SiteSelector (tenant as object)
  const sitesForSelector = sites.map(site => ({
    ...site,
    tenant: {
      id: site.tenant,
      name: site.tenant_name || '',
      code: tenants.find(t => t.id === site.tenant)?.code || ''
    }
  }));

  // Convert currentSite for SiteSelector
  const currentSiteForSelector = currentSite ? {
    ...currentSite,
    tenant: {
      id: currentSite.tenant,
      name: currentSite.tenant_name || '',
      code: tenants.find(t => t.id === currentSite.tenant)?.code || ''
    }
  } : null;

  // Convert sites for UserAssignment (tenant as object)
  const sitesForAssignment = sites.map(site => ({
    ...site,
    tenant: {
      id: site.tenant,
      name: site.tenant_name || '',
      code: tenants.find(t => t.id === site.tenant)?.code || ''
    }
  }));

  const tabs = [
    {
      id: 'selector',
      label: 'Sélecteur de sites',
      icon: <ArrowLeftRight className="w-5 h-5" />,
      component: <SiteSelector sites={sitesForSelector} onSiteSwitch={handleSiteSwitch} currentSite={currentSiteForSelector} />
    },
    {
      id: 'management',
      label: 'Gestion des sites',
      icon: <Building2 className="w-5 h-5" />,
      component: <SiteManagement sites={sites} tenants={tenants} onSitesUpdate={fetchSites} />
    },
    {
      id: 'assignment',
      label: 'Attribution utilisateurs',
      icon: <Users className="w-5 h-5" />,
      component: <UserAssignment sites={sitesForAssignment} />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-orange-500 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Gestion Multi-Sites</h1>
            </div>
            <div className="flex items-center space-x-4">
              {currentSite && (
                <div className="bg-orange-100 px-3 py-2 rounded-lg">
                  <span className="text-sm font-medium text-orange-800">
                    Site actuel: {currentSite.name} ({currentSite.code})
                  </span>
                </div>
              )}
              {isTenantUser && currentTenantUser && (
                <div className="bg-blue-100 px-3 py-2 rounded-lg">
                  <span className="text-sm font-medium text-blue-800">
                    Tenant: {currentTenantUser.tenant_name || `ID ${currentTenantUser.tenant_id}`} 
                    ({currentTenantUser.role})
                  </span>
                </div>
              )}
              {isSiteUser && currentUserProfile && (
                <div className="bg-green-100 px-3 py-2 rounded-lg">
                  <span className="text-sm font-medium text-green-800">
                    Sites assignés: {currentUserProfile.sites?.length || 0} site(s) 
                    ({currentUserProfile.role?.name})
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message informatif pour les utilisateurs tenant et site */}
        {(isTenantUser || isSiteUser) && (
          <div className={`mb-6 border rounded-lg p-4 ${
            isTenantUser ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className={`h-5 w-5 ${isTenantUser ? 'text-blue-400' : 'text-green-400'}`} />
              </div>
              <div className="ml-3">
                <p className={`text-sm ${isTenantUser ? 'text-blue-700' : 'text-green-700'}`}>
                  {isTenantUser ? (
                    <>
                      <strong>Mode Tenant :</strong> Vous ne voyez que les sites et tenants associés à votre organisation. 
                      {currentTenantUser && (
                        <span> Tenant actuel : {currentTenantUser.tenant_name || `ID ${currentTenantUser.tenant_id}`}</span>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>Mode Site :</strong> Vous ne voyez que les sites qui vous sont assignés. 
                      {currentUserProfile?.sites && (
                        <span> Sites assignés : {currentUserProfile.sites.length} site(s)</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Statistics */}
        <DashboardStats currentSite={currentSite} />
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow">
            {tabs.find(tab => tab.id === activeTab)?.component}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSiteManagement;
